/**
 * 場域管理頁面（僅 admin）。
 * 對應原生版 app.js viewSites + openSiteForm。
 *
 * API：
 *   GET    /api/v1/sites                                  — 場域清單
 *   POST   /api/v1/sites                                  — 新增場域
 *   PATCH  /api/v1/sites/{encodeURIComponent(code)}       — 重新命名
 *   DELETE /api/v1/sites/{encodeURIComponent(code)}       — 刪除場域
 *   GET    /api/v1/devices                                — 裝置清單（算未指派）
 */

import { useState, type FormEvent } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { MapPin, Trash2 } from 'lucide-react';
import { apiGet, apiPost, apiPatch, apiDelete } from '@/lib/api';
import { order } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { DataTable, type Column } from '@/components/DataTable';
import { Drawer } from '@/components/Drawer';
import type { Site, Device } from '@/types';

/* ── 型別：表單送出 ── */
interface SiteFormPayload {
  code?: string;
  name: string;
}

/* ── 場域清單欄位定義 ── */
function useSiteColumns(
  devices: Device[],
  onEdit: (s: Site) => void,
  onDelete: (s: Site) => void,
): Column<Site>[] {
  /** 依場域代號分組裝置 */
  const byCode = new Map<string, Device[]>();
  for (const d of devices) {
    if (d.site_code) {
      const arr = byCode.get(d.site_code) ?? [];
      arr.push(d);
      byCode.set(d.site_code, arr);
    }
  }

  return [
    {
      key: 'code',
      header: '代號',
      cell: (s) => <span className="font-mono text-[11.5px]">{s.code}</span>,
    },
    {
      key: 'name',
      header: '名稱',
      cell: (s) => s.name,
    },
    {
      key: 'devices',
      header: '綁定裝置',
      cell: (s) => {
        const ds = byCode.get(s.code) ?? [];
        if (!ds.length) return <span className="text-muted-foreground">尚未綁定裝置</span>;
        return <span className="text-muted-foreground text-[12px]">{ds.map((d) => d.label || d.device_id).join('、')}</span>;
      },
    },
    {
      key: 'online',
      header: '線上',
      cell: (s) => {
        const ds = byCode.get(s.code) ?? [];
        const online = ds.filter((d) => d.online).length;
        return (
          <Badge variant={online === ds.length && ds.length > 0 ? 'success' : 'muted'}>
            {online}/{ds.length}
          </Badge>
        );
      },
    },
    {
      key: 'actions',
      header: '',
      cell: (s) => {
        const ds = byCode.get(s.code) ?? [];
        return (
          <div className="flex justify-end gap-1.5">
            <Button size="sm" variant="ghost" onClick={() => onEdit(s)}>
              重新命名
            </Button>
            <Button
              size="sm"
              variant="ghost"
              title={ds.length ? '仍有裝置綁定，需先改派' : '刪除此場域'}
              onClick={() => onDelete(s)}
            >
              <Trash2 className="h-3.5 w-3.5" />
              <span>刪除</span>
            </Button>
          </div>
        );
      },
    },
  ];
}

export default function SitesPage() {
  const qc = useQueryClient();

  /* ── 資料查詢 ── */
  const sitesQ = useQuery({
    queryKey: ['sites'],
    queryFn: () => apiGet<Site[]>('/api/v1/sites'),
  });
  const devicesQ = useQuery({
    queryKey: ['devices'],
    queryFn: () => apiGet<Device[]>('/api/v1/devices'),
  });

  const sites = sitesQ.data ?? [];
  const devices = devicesQ.data ?? [];

  /* ── 抽屜狀態 ── */
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState<Site | null>(null);

  /* ── 表單欄位 ── */
  const [formCode, setFormCode] = useState('');
  const [formName, setFormName] = useState('');
  const [formError, setFormError] = useState('');

  const isNew = editing === null && drawerOpen;

  const openCreate = () => {
    setEditing(null);
    setFormCode('');
    setFormName('');
    setFormError('');
    setDrawerOpen(true);
  };
  const openEdit = (s: Site) => {
    setEditing(s);
    setFormCode(s.code);
    setFormName(s.name);
    setFormError('');
    setDrawerOpen(true);
  };
  const closeDrawer = () => setDrawerOpen(false);

  /* ── 新增 ── */
  const createMut = useMutation({
    mutationFn: (p: SiteFormPayload) => apiPost<Site>('/api/v1/sites', p),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sites'] });
      closeDrawer();
    },
    onError: (e: Error) => setFormError(e.message),
  });

  /* ── 重新命名 ── */
  const renameMut = useMutation({
    mutationFn: ({ code, name }: { code: string; name: string }) =>
      apiPatch<Site>(`/api/v1/sites/${encodeURIComponent(code)}`, { name }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sites'] });
      closeDrawer();
    },
    onError: (e: Error) => setFormError(e.message),
  });

  /* ── 刪除 ── */
  const deleteMut = useMutation({
    mutationFn: (code: string) =>
      apiDelete(`/api/v1/sites/${encodeURIComponent(code)}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['sites'] });
    },
  });

  const handleDelete = (s: Site) => {
    if (!window.confirm(`確定刪除場域 ${s.code}（${s.name}）？`)) return;
    deleteMut.mutate(s.code, {
      onError: (e: Error) => alert(e.message),
    });
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    setFormError('');
    if (editing) {
      renameMut.mutate({ code: editing.code, name: formName });
    } else {
      createMut.mutate({ code: formCode, name: formName });
    }
  };

  const submitting = createMut.isPending || renameMut.isPending;

  /* ── 未指派場域的裝置 ── */
  const unassigned = devices.filter((d) => !d.site_code);

  /* ── 表格欄位 ── */
  const columns = useSiteColumns(devices, openEdit, handleDelete);

  return (
    <>
      {/* 場域清單卡片 */}
      <Card className="reveal admin-data-card overflow-hidden" style={order(0)}>
        <CardHeader className="pb-5">
          <div>
            <CardTitle>場域（{sites.length}）</CardTitle>
            <CardDescription>共 {sites.length} 個場域；事件歸屬鎖定發生當下的場域</CardDescription>
          </div>
          <Button onClick={openCreate}>
            <MapPin className="h-[15px] w-[15px]" />
            <span>新增場域</span>
          </Button>
        </CardHeader>
        <DataTable<Site>
          rows={sites}
          columns={columns}
          rowKey={(s) => s.code}
          loading={sitesQ.isLoading}
          error={sitesQ.error}
          empty="尚無場域。點右上角「新增場域」建立第一個場域。"
        />
      </Card>

      {/* 未指派場域的裝置警示 */}
      {unassigned.length > 0 && (
        <Card className="reveal mt-4" style={order(1)}>
          <CardHeader className="pb-2">
            <div>
              <CardTitle>未指派場域的裝置</CardTitle>
              <CardDescription>
                這些裝置產生的事件會標成「待確認」，請到裝置頁指派場域
              </CardDescription>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {unassigned.map((d) => (
                <Badge key={d.device_id} variant="warn">
                  {d.label || d.device_id}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* 新增 / 重新命名 Drawer */}
      <Drawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        title={isNew ? '新增場域' : `重新命名 ${editing?.code ?? ''}`}
      >
        <form onSubmit={handleSubmit} className="flex flex-col gap-5">
          {/* 場域代號 */}
          <label className="flex flex-col gap-1.5">
            <span className="text-sm font-medium">場域代號</span>
            <Input
              value={formCode}
              onChange={(e) => setFormCode(e.target.value)}
              disabled={!isNew}
              placeholder="例如 SITE-D"
              required
            />
            <span className="text-[11.5px] text-muted-foreground">
              {isNew ? '建立後不可更改：事件歷史以此代號歸屬' : '代號不可更改'}
            </span>
          </label>

          {/* 場域名稱 */}
          <label className="flex flex-col gap-1.5">
            <span className="text-sm font-medium">場域名稱</span>
            <Input
              value={formName}
              onChange={(e) => setFormName(e.target.value)}
              placeholder="例如 台中辦公室"
              required
            />
          </label>

          {/* 錯誤訊息 */}
          {formError && (
            <p className="text-[13px] text-destructive">{formError}</p>
          )}

          <Button type="submit" disabled={submitting} block>
            {submitting ? '處理中…' : isNew ? '建立場域' : '儲存'}
          </Button>
        </form>
      </Drawer>
    </>
  );
}
